import type { ZoneOpts } from '../../game/netHooks';
import type { Progression } from '../../game/Progression';
import type { RewardsState } from '../../ui/app/store';

export type AnnounceStyle = 'boss' | 'mechanic' | 'info' | 'warning' | 'success';
export type ActivityKind = 'story' | 'rift' | 'colossus' | 'deepvault' | 'siege' | 'crucible';

/**
 * What instance scripts need from the game client. `Game` provides it in the
 * browser (see client.ts); tests pass a recording fake so the scripts run on
 * a headless ZoneRuntime.
 */
export interface InstanceEnv {
  /** The local character's progression (null when no character is loaded). */
  readonly progression: Progression | null;
  /** Presentation is available (false in headless tests). */
  readonly visual: boolean;
  announce(text: string, style?: AnnounceStyle): void;
  toast(text: string, color?: string): void;
  /** Opens the rewards overlay. */
  rewards(r: RewardsState): void;
  /** Moves this client to the next zone of the same run (floor / gate); applied after the frame. */
  transition(zone: string, opts: ZoneOpts): void;
  /** Leaves the instance (back to where the player entered from). */
  leave(): void;
  /** Clears the death screen and the grey-out after a scripted revive. */
  revived(): void;
  /** Brings the daily / weekly records up to date before lockouts are read or written. */
  syncPeriods(): void;
  /** Persists the save soon (after rewards and lockouts change). */
  save(): void;
  /** Per-client memory that survives zone changes within a session (run milestones, revives used…). */
  readonly memo: Map<string, unknown>;
}

/** Returns true the first time `key` is seen by this client (rewards are granted exactly once). */
export function once(env: InstanceEnv, key: string): boolean {
  if (env.memo.has(key)) return false;
  env.memo.set(key, true);
  return true;
}

// ---------------------------------------------------------------- views (plain data for the HUD)

export type Tone = 'gold' | 'blue' | 'red' | 'green' | 'violet' | 'grey';

/** A button the HUD shows; `id` is handed back to the script's `action()`. */
export interface HudAction {
  id: string;
  label: string;
  tone?: 'gold' | 'plain' | 'danger';
  /** Ask before running (e.g. leaving a run early). */
  confirm?: string;
  disabled?: boolean;
}

export interface HudView {
  kind: ActivityKind;
  title: string;
  subtitle: string;
  timer: { label: string; seconds: number; warn: boolean } | null;
  /** Rift progress gauge, 0–100. */
  gauge: number | null;
  /** Gauge marks (portal thresholds). */
  marks: number[];
  revives: { n: number; max: number; label: string } | null;
  score: { red: number; blue: number; round: number; maxRounds: number; mine: 'red' | 'blue' | null } | null;
  /** Gate progress pips. */
  pips: { label: string; state: 'done' | 'current' | 'todo' }[];
  badges: { text: string; tone: Tone }[];
  /** Big centre text (countdowns, round results). */
  banner: string | null;
  actions: HudAction[];
}

export interface DeathView {
  title: string;
  text: string;
  /** Seconds shown as the big yellow countdown. */
  countdown: number | null;
  actions: HudAction[];
}

export interface ResultView {
  title: string;
  subtitle: string;
  tone: 'win' | 'loss' | 'neutral';
  lines: string[];
  actions: HudAction[];
}

export const emptyHud = (kind: ActivityKind, title: string, subtitle = ''): HudView => ({
  kind,
  title,
  subtitle,
  timer: null,
  gauge: null,
  marks: [],
  revives: null,
  score: null,
  pips: [],
  badges: [],
  banner: null,
  actions: [],
});

export function clockText(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
